import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Group from 'components/utils/Group'
import LoadingBox from 'components/Common/Loading/LoadingBox'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import SocieteCampaignDisclaimerText from '../SocieteCampaignDisclaimerText'
import SocieteCampaignDisclaimerLink from '../SocieteCampaignDisclaimerLink'
import SocieteCampaignDisclaimerTooltip from '../SocieteCampaignDisclaimerTooltip'
import TextLink from 'LuxKit/TextLink'
import LuxuryEscapesStampGrid from 'LuxKit/LuxuryEscapesStampGrid'
import LuxuryEscapesSmallLogo from 'components/App/Header/Logo/logos/LuxuryEscapesSmallLogo'
import LoyaltyProgramNameLogo from 'loyalty/components/LoyaltyProgramNameLogo'
import usePersonalPortalHref from 'hooks/usePersonalPortalHref'
import { rem } from 'polished'
import React from 'react'
import styled from 'styled-components'
import useEmployeeOrders from 'businessTraveller/hooks/useEmployeeOrders'
import { useSocieteInterpolate } from 'loyalty/hooks/useSocieteInterpolate'
import { LOYALTY_LANDING_PAGE } from 'loyalty/constants/loyaltyRoutes'

const Card = styled.div`
  background: ${props => props.theme.palette.product.loyalty.gold.gradientPrimaryBackground};
  color: ${props => props.theme.palette.product.loyalty.gold.normalContrast};
  border-radius: ${rem(8)};
  padding: ${rem(24)};
`

interface Props {
  campaign: App.SocieteCampaignPayload
  societeSetting: App.BusinessTraveller.BusinessSettingsSociete
  linkedCreditAccount: App.LoyaltyLinkedCreditAccount | undefined
  onLearnMoreClick?: () => void
}

function SocieteCampaignGoldCardContent(props: Props) {
  const { campaign, societeSetting, linkedCreditAccount, onLearnMoreClick } = props
  const interpolate = useSocieteInterpolate()
  const personalPortalHref = usePersonalPortalHref()

  const threshold = societeSetting.value.gold.threshold

  const { total: upcomingTotal, isLoading: isLoadingUpcoming } = useEmployeeOrders({
    timeframe: 'upcoming',
    page: 0,
    limit: 1,
    filterBy: 'traveller',
  })
  const { total: qualifiedTotal, isLoading: isLoadingQualified } = useEmployeeOrders({
    timeframe: 'qualificationWindow',
    page: 0,
    limit: 1,
    filterBy: 'traveller',
  })

  if (isLoadingUpcoming || isLoadingQualified) {
    return <LoadingBox height={280} borderRadius="L" />
  }

  return <Card>
    <VerticalSpacer gap={20}>
      <Group
        direction="horizontal"
        verticalAlign="start"
        gap={4}
      >
        <LuxuryEscapesSmallLogo height={21} fill="currentColor" />
        <LoyaltyProgramNameLogo width={84} height={19} />
      </Group>
      <VerticalSpacer gap={8}>
        {campaign.hero.titleHighlight && <Heading variant="heading3">{interpolate(campaign.hero.titleHighlight)}</Heading>}
        <BodyText variant="medium">
          {interpolate(campaign.hero.subtitle, {
            qualificationEndDate: (vars) => <BodyText as="span" variant="medium" weight="bold">{vars.qualificationEndDate}</BodyText>,
          })}
        </BodyText>
      </VerticalSpacer>
      <VerticalSpacer gap={12}>
        <LuxuryEscapesStampGrid count={qualifiedTotal} total={threshold} size={48} dashedColour="tier-gold-contrast" dashedOpacity={0.4} />
        <Group direction="horizontal" gap={8} verticalAlign="center">
          <BodyText variant="small">
            <BodyText as="span" variant="small" weight="bold">{upcomingTotal}</BodyText> pending
          </BodyText>
          <BodyText variant="small" weight="bold">•</BodyText>
          <BodyText variant="small">
            <BodyText as="span" variant="small" weight="bold">{qualifiedTotal}</BodyText> completed
          </BodyText>
        </Group>
      </VerticalSpacer>
      <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center" gap={8}>
        {/* No linked credit account → user isn't a Société member yet */}
        {!linkedCreditAccount && <Group direction="horizontal" verticalAlign="center" gap={4}>
          <SocieteCampaignDisclaimerText>
            Join{' '}
            <SocieteCampaignDisclaimerLink
              text="Société"
              colour="tier-gold-contrast"
              href={`${personalPortalHref}/societe`}
            />{' '}
            to enjoy this benefit
          </SocieteCampaignDisclaimerText>
          <SocieteCampaignDisclaimerTooltip />
        </Group>}
        <TextLink size="small" colour="tier-gold-contrast" to={`/${LOYALTY_LANDING_PAGE}`} onClick={onLearnMoreClick} style={{ marginLeft: 'auto' }}>Learn more</TextLink>
      </Group>
    </VerticalSpacer>
  </Card>
}

export default SocieteCampaignGoldCardContent
